// @flow
import { instantMeiliSearch } from '@meilisearch/instant-meilisearch';
import React, { PureComponent } from 'react';
import { connectHits, Configure, InstantSearch, connectStateResults } from 'react-instantsearch-dom';

import DropdownRadioInput from 'components/fragments/input/DropdownRadioInput';
import OpenStreetMap from 'components/fragments/map/OpenStreetMap';
import Pagination from 'components/newsletter/Pagination';
import { MEILISEARCH_KEY, MEILISEARCH_URL } from 'constants/api';
import { MOBILE_SIZE } from 'constants/app';
import { CLUB_PER_PAGE, CENTRE_RUGBY_LAT, CENTRE_RUGBY_LONG } from 'constants/clubConstants';
import { SORT_NAME, SORT_DISTANCE } from 'constants/sortConstants';
import HeaderFindClubContainer from 'containers/header/HeaderFindClubContainer';
import { withHelmet } from 'infrastructure/seo/Helmet';
import type { ClubPreviewType } from 'types/Club';
import type { Status } from 'types/Status';
import { hitToClubPreview } from 'utils/clubUtils';
import { dynamicClassName } from 'utils/dynamicClassName';
import { getMetaBySlug } from 'utils/seoUtils';

import Clubs from './ClubFinderClubs';

const searchClient = instantMeiliSearch(MEILISEARCH_URL, MEILISEARCH_KEY, { paginationTotalHits: 5000 });

export type StateProps = {
  query: string,
  status: Status,
  distance: string,
  practices: string[],
  competitions: string[],
  ligue: number
};

export type DispatchProps = {
  fetchClubsPreview: () => void,
  activateDistance: () => void,
  deactivateDistance: () => void,
  setQuery: (query: string) => void,
  setPractices: (rugbyTypes: string[]) => void,
  reinitialiseSearch: () => void
};

type Props = {
  clubs: ClubPreviewType,
  history: any,
  location: {
    state: {
      query: string,
      rugbyTypes: string,
      autour: boolean,
      lat: number,
      lng: number,
      municipality: string
    }
  }
} & StateProps &
  DispatchProps;

type State = {
  currentPage: number,
  minimum: number,
  maximum: number,
  isMaximumLastPage: boolean,
  distances: string[],
  sort: SORT_NAME | SORT_DISTANCE,
  userLatitude: number | false,
  userLongitude: number | false,
  toggleMap: boolean,
  geolocation: boolean,
  isLoading: boolean,
  lat: number | false,
  lng: number | false,
  municipality: string | false
};

const SubTitle = connectHits(({ hits, maxDistance, userLatitude, userLongitude }) => {
  let filterHits = hits;

  return <p className="ft-h6 ft-up">{filterHits.length} club(s) trouvé(s)</p>;
});

const LoadingSearch = connectStateResults(({ searching, children }) => (
  <div>
    <div>{children}</div>
  </div>
));

const Map = connectHits(({ hits, userLatitude, userLongitude, maxDistance, municipality }) => {
  return (
    <OpenStreetMap
      lat={userLatitude}
      long={userLongitude}
      municipality={municipality}
      userMarker={false}
      width={'425'}
      height={'100%'}
      clubs={hits.filter(hit => !!hit._geoloc).map(hitToClubPreview)}
    />
  );
});

const ClubsPagination = connectHits(
  ({ currentPage, hits, isMaximumLastPage, onPageChanged, maxDistance, userLatitude, userLongitude }) => {
    let filterHits = hits;
    if (maxDistance) {
      filterHits = filterHits.map(hitToClubPreview);
    }

    return (
      filterHits.length > 1 && (
        <Pagination
          current={currentPage}
          minimum={1}
          maximum={Math.ceil(filterHits.length / CLUB_PER_PAGE)}
          isMaximumLastPage={isMaximumLastPage}
          onPageChanged={onPageChanged}
        />
      )
    );
  }
);

class ClubFinderResult extends PureComponent<Props, State> {
  state: State = {
    currentPage: 1,
    minimum: 1,
    maximum: 4,
    isMaximumLastPage: true,
    rugbyTypes: [],
    competitionTypes: [],
    distances: [],
    sort: SORT_NAME,
    userLatitude: false,
    userLongitude: false,
    lat: false,
    lng: false,
    municipality: false,
    toggleMap: false,
    geolocation: false,
    isLoading: false
  };
  _list: ?HTMLElement;

  componentDidMount() {
    const { state } = this.props.location;

    if (!this.props.query && state && state.query) {
      this.props.setQuery(state.query);
      const stateCopy = { ...state };
      delete stateCopy.query;
      this.props.history.replace({ state: stateCopy });
    }
    if (this.props.location && this.props.location.state && this.props.location.state.autour) {
      this.setState({ sort: SORT_DISTANCE });
    }

    if (
      this.props.location &&
      this.props.location.state &&
      this.props.location.state.municipality &&
      this.props.location.state.lat
    ) {
      this.setState({
        lat: this.props.location.state.lat,
        lng: this.props.location.state.lng,
        municipality: this.props.location.state.municipality,
        geolocation: true,
        sort: SORT_DISTANCE
      });
      this.props.activateDistance();
      this.render();
    } else {
      if (navigator.geolocation) {
        this.setState({ isLoading: true });
        navigator.geolocation.getCurrentPosition(
          position => {
            this.setState({
              userLatitude: position.coords.latitude,
              userLongitude: position.coords.longitude,
              geolocation: true,
              isLoading: false
            });
            this.props.activateDistance();
            this.render();
          },
          e => {
            this.setState({
              isLoading: false
            });
          }
        );
      }
    }
    this.props.fetchClubsPreview();
  }

  componentWillUnmount() {
    this.props.reinitialiseSearch();
  }

  componentDidUpdate(prevProps: Props) {
    const { distance, practices, query } = this.props;
    const { currentPage } = this.state;

    if (
      (query !== prevProps.query && currentPage !== 1) ||
      distance !== prevProps.distance ||
      practices !== prevProps.practices
    ) {
      this.setState({ currentPage: 1 });
    }

    if (
      this.state.municipality &&
      (!this.props.location || !this.props.location.state || !this.props.location.state.municipality)
    ) {
      this.setState({
        lat: false,
        lng: false,
        municipality: false,
        geolocation: false,
        sort: SORT_NAME,
        isLoading: true
      });
      this.props.deactivateDistance();
    }

    if (
      this.props.location &&
      this.props.location.state &&
      this.props.location.state.municipality &&
      (!this.state.municipality || this.state.municipality !== this.props.location.state.municipality)
    ) {
      this.setState({
        lat: this.props.location.state.lat,
        lng: this.props.location.state.lng,
        municipality: this.props.location.state.municipality,
        geolocation: true,
        sort: SORT_DISTANCE,
        isLoading: true
      });
      this.props.activateDistance();
    }
  }

  onPageChanged = (currentPage: number) => {
    if (this._list) {
      this._list.scrollIntoView(true);
    }
    this.setState({ currentPage });
  };

  handleSortChanged = (sort: SORT_NAME | SORT_DISTANCE) => {
    this.setState({ sort });
  };

  renderMap = maxDistance => {
    if (window.innerWidth < MOBILE_SIZE && !this.state.toggleMap) {
      return null;
    }

    return (
      <Map
        userLatitude={this.state.lat || this.state.userLatitude || CENTRE_RUGBY_LAT}
        userLongitude={this.state.lng || this.state.userLongitude || CENTRE_RUGBY_LONG}
        maxDistance={maxDistance}
        municipality={this.state.municipality}
      />
    );
  };

  toggleMap = () => {
    this.setState({
      toggleMap: !this.state.toggleMap
    });
  };

  render() {
    const { query, practices, competitions, distance, ligue } = this.props;
    const { userLatitude, userLongitude, lat, lng, geolocation, currentPage, isMaximumLastPage, sort } = this.state;

    const currentDistance = distance === '' ? 99999 : distance;
    const d = parseInt(currentDistance || 80, 10);
    const divClassName = dynamicClassName('result-club');
    this.state.toggleMap && divClassName.add('has-map');

    let filters = `structureParentId=${ligue}`;
    if (practices && practices.length > 0) {
      filters += ' AND ( ';
      practices.forEach((p, index) => (filters += ` ${index !== 0 ? 'AND ' : ''}pratiques='${p}' `));
      filters += ' )';
    }

    if (competitions.length > 0) {
      if (filters) {
        filters += ' AND (';
      }
      competitions.forEach(
        (p, index) =>
          (filters += ` ${index !== 0 ? 'AND ' : ''}competitions="${p /* p.StructureOrganisatrice.nom} $ ${p.nom */}" `)
      );
      filters += ' )';
    }
    let sortIndex = false;
    const latitude = lat || userLatitude;
    const longitude = lng || userLongitude;

    if (latitude !== false && longitude !== false && d && !isNaN(d)) {
      if (d !== 100000) {
        filters +=
          (filters.length > 0 ? ' AND ' : '') + '_geoRadius(' + latitude + ', ' + longitude + ', ' + d * 1000 + ')';
      }
      sortIndex = '_geoPoint(' + latitude + ', ' + longitude + '):asc';
    }

    return (
      <InstantSearch indexName={sortIndex ? 'Clubs:' + sortIndex : 'Clubs'} searchClient={searchClient}>
        <Configure query={query} distinct hitsPerPage={5000} filters={filters} />

        <div className="header" style={{ zIndex: 9 }}>
          <HeaderFindClubContainer location={this.props.location} />
        </div>

        <LoadingSearch>
          <div className={divClassName.build()}>
            <div className="container">
              <div className="row mb-6 mb-md-7 mb-md-8">
                <div className="col col-md-7 col-lg-5 result-club__list" ref={r => (this._list = r)}>
                  <div className="result-club__header clearfix mt-2 mt-md-4 mt-lg-5 mb-2 mb-md-4">
                    <span className="btn btn--primary js-toggleMap" role="button" tabIndex={0} onClick={this.toggleMap}>
                      <i className="icon icon-place is-inline" />
                      <span>Carte</span>
                    </span>

                    <SubTitle
                      maxDistance={geolocation && !isNaN(d) && d}
                      userLatitude={userLatitude || lat}
                      userLongitude={userLongitude || lng}
                    />

                    <DropdownRadioInput
                      id="dropdown"
                      prelabel="Trier par : "
                      maj={false}
                      title={false}
                      radio={true}
                      marge={false}
                      packed={true}
                      label={this.state.sort === SORT_NAME ? 'Ordre alphabétique' : 'Distance'}
                      options={[
                        { value: SORT_NAME, label: 'Ordre alphabétique' },
                        { value: SORT_DISTANCE, label: 'Distance' }
                      ]}
                      inputChanged={this.handleSortChanged}
                    />
                  </div>

                  <Clubs
                    clubPerPage={CLUB_PER_PAGE}
                    currentPage={currentPage}
                    practices={practices}
                    sort={sort}
                    maxDistance={!isNaN(d) && d}
                    userLatitude={userLatitude || lat}
                    userLongitude={userLongitude || lng}
                  />

                  <ClubsPagination
                    currentPage={currentPage}
                    isMaximumLastPage={isMaximumLastPage}
                    onPageChanged={this.onPageChanged}
                    maxDistance={!isNaN(d) && d}
                    userLatitude={userLatitude || lat}
                    userLongitude={userLongitude || lng}
                  />
                </div>
                <div className="result-club__maps">
                  <span
                    className="btn btn--primary js-toggleMap"
                    role="button"
                    tabIndex={0}
                    onClick={this.toggleMap}
                    style={{ zIndex: 20 }}
                  >
                    <i className="icon icon-list is-inline" />
                    <span>Liste</span>
                  </span>

                  {this.renderMap(!isNaN(d) && d)}
                </div>
              </div>
            </div>
          </div>
        </LoadingSearch>
      </InstantSearch>
    );
  }
}

export default withHelmet(ClubFinderResult)((Helmet, pageProps) => {
  const meta = getMetaBySlug(pageProps.location.pathname);
  return (
    <Helmet>
      <title>{meta.title(pageProps.query)}</title>
      <meta name={'description'} content={meta.description()} />
      <meta property={'og:title'} content={meta.title(pageProps.query)} />
      <meta property={'og:description'} content={meta.description()} />
    </Helmet>
  );
});
